import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

import { getConfigWithToken } from "../config/config";
import { getSuperAdminUserById } from "../config/url";
import classes from "./SuperAdminList.module.css";

const formatDate = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleString("en-US", {
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const SuperAdminUserDetails = () => {
	const { accountType, id } = useParams();
	const navigate = useNavigate();
	const token = useSelector((state) => state?.user?.user?.token) || localStorage.getItem("user_id");
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [userRow, setUserRow] = useState(null);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
			},
		}),
		[token],
	);

	const fetchUser = useCallback(async () => {
		setIsLoading(true);
		setHasError(false);
		try {
			const response = await axios.get(getSuperAdminUserById(accountType, id), requestConfig);
			setUserRow(response?.data?.data || null);
		} catch {
			setHasError(true);
			setUserRow(null);
		} finally {
			setIsLoading(false);
		}
	}, [accountType, id, requestConfig]);

	useEffect(() => {
		fetchUser();
	}, [fetchUser]);

	return (
		<div className={classes.page}>
			<button className={classes.backButton} onClick={() => navigate("/users")} type="button">
				<ArrowLeft size={16} />
				Back to Users
			</button>
			<h1 className={classes.title}>User Details</h1>

			{isLoading ? (
				<p className={classes.subtitle}>Loading...</p>
			) : hasError || !userRow ? (
				<div className={classes.errorBox}>
					<p>User could not be loaded.</p>
					<button className={classes.retryButton} onClick={fetchUser} type="button">
						Retry
					</button>
				</div>
			) : (
				<div className={classes.detailCard}>
					<div>
						<p className={classes.detailLabel}>Name</p>
						<p className={classes.detailValue}>{userRow.name}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Email</p>
						<p className={classes.detailValue}>{userRow.email || "-"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Account Type</p>
						<p className={classes.detailValue}>{userRow.accountType === "admin" ? "Admin" : "User"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Role</p>
						<p className={classes.detailValue}>{userRow.role || "-"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Status</p>
						<p className={classes.detailValue}>{userRow.isActive ? "Active" : "Inactive"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Created</p>
						<p className={classes.detailValue}>{formatDate(userRow.createdAt)}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>ID</p>
						<p className={classes.detailValue}>{userRow.id}</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default SuperAdminUserDetails;
